/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { animate, style, transition, trigger } from "@angular/animations";
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Output,
	type TemplateRef,
	ViewEncapsulation,
	input,
} from "@angular/core";
import { FloatingComponent } from "../../floating-component";

export interface PopoverOption {
	label: string;
	value: unknown;
	data?: unknown;
	icon?: string;
	selected?: boolean;
	disabled?: boolean;
	color?:
		| "brand"
		| "primary"
		| "success"
		| "warning"
		| "error"
		| "info"
		| "gray";
}

export interface GroupedPopoverOption {
	options: PopoverOption[];
}

@Component({
	selector: "cb-popover",
	templateUrl: "./popover.component.html",
	styleUrl: "./popover.component.scss",
	animations: [
		trigger("PopoverAnimation", [
			transition(":enter", [
				style({ opacity: 0, transform: "translateY(-0.6rem)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "1", transform: "translateY(0)" }),
				),
			]),
			transition(":leave", [
				style({ opacity: 1, transform: "translateY(0)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "0", transform: "translateY(-0.5rem)" }),
				),
			]),
		]),
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	standalone: false,
})
export class PopoverComponent extends FloatingComponent {
	/**
	 * The position of the tooltip.
	 */
	public readonly position = input<"top" | "bottom" | "left" | "right">("left");

	/**
	 * The left position of the tooltip.
	 */
	public readonly leftPosition = input(0);

	/**
	 * The top position of the tooltip.
	 */
	public readonly topPosition = input(0);

	/**
	 * The alignment of the tooltip.
	 */
	public readonly alignment = input<"start" | "center" | "end">("center");

	/**
	 * The template of the tooltip.
	 */
	public readonly template = input<TemplateRef<unknown> | null>(null);

	/**
	 * The options of the tooltip.
	 */
	public readonly options = input<PopoverOption[]>([]);

	/**
	 * The grouped options of the tooltip.
	 */
	public readonly groupedOptions = input<GroupedPopoverOption[]>([]);

	/**
	 * If the arrow should be shown.
	 */
	public readonly showArrow = input(true);

	/**
	 * The context to pass to the template.
	 */
	public readonly context = input<Record<string, unknown>>();

	/**
	 * The template to use for the options.
	 */
	public readonly optionsTemplate = input<TemplateRef<unknown> | null>(null);

	/**
	 * The event emitted when an option is clicked.
	 */
	@Output() public optionClickedEvent: EventEmitter<PopoverOption> =
		new EventEmitter<PopoverOption>();

	/**
	 * Triggered when an option is clicked.
	 * @param option
	 */
	public optionClicked(option: PopoverOption): void {
		if (option.disabled) return;
		this.optionClickedEvent.emit(option);
	}
}
